import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Download } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.4, 0.25, 1] }
  })
};

export default function Hero() {
  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Geometric Background - Diamond + Grid */}
      <div className="absolute inset-0">
        <svg
          className="w-full h-full"
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <radialGradient id="heroGlow" cx="50%" cy="40%" r="50%">
              <stop offset="0%" stopColor="#B8102E" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#B8102E" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="1200" height="800" fill="#0A0A0A" />
          <rect width="1200" height="800" fill="url(#heroGlow)" />

          {/* Fine grid */}
          {[...Array(21)].map((_, i) => (
            <line
              key={`vg${i}`}
              x1={i * 60}
              y1="0"
              x2={i * 60}
              y2="800"
              stroke="#B8102E"
              strokeWidth="0.3"
              opacity="0.06"
            />
          ))}
          {[...Array(14)].map((_, i) => (
            <line
              key={`hg${i}`}
              x1="0"
              y1={i * 60}
              x2="1200"
              y2={i * 60}
              stroke="#B8102E"
              strokeWidth="0.3"
              opacity="0.06"
            />
          ))}

          {/* Diamond shape - baseball diamond metaphor */}
          <polygon
            points="600,120 860,400 600,680 340,400"
            fill="none"
            stroke="#B8102E"
            strokeWidth="1"
            opacity="0.12"
          />
          <polygon
            points="600,220 770,400 600,580 430,400"
            fill="none"
            stroke="#B8102E"
            strokeWidth="0.6"
            opacity="0.08"
          />

          {/* Node points */}
          <circle cx="600" cy="120" r="3" fill="#B8102E" opacity="0.3" />
          <circle cx="860" cy="400" r="3" fill="#B8102E" opacity="0.3" />
          <circle cx="600" cy="680" r="3" fill="#B8102E" opacity="0.3" />
          <circle cx="340" cy="400" r="3" fill="#B8102E" opacity="0.3" />
          <circle cx="600" cy="400" r="5" fill="#B8102E" opacity="0.15" />

          {/* Cross lines through center */}
          <line x1="600" y1="120" x2="600" y2="680" stroke="#B8102E" strokeWidth="0.3" opacity="0.1" />
          <line x1="340" y1="400" x2="860" y2="400" stroke="#B8102E" strokeWidth="0.3" opacity="0.1" />

          {/* Diagonal connection lines */}
          <line x1="340" y1="400" x2="600" y2="120" stroke="#B8102E" strokeWidth="0.3" opacity="0.06" />
          <line x1="600" y1="120" x2="860" y2="400" stroke="#B8102E" strokeWidth="0.3" opacity="0.06" />
          <line x1="860" y1="400" x2="600" y2="680" stroke="#B8102E" strokeWidth="0.3" opacity="0.06" />
          <line x1="600" y1="680" x2="340" y2="400" stroke="#B8102E" strokeWidth="0.3" opacity="0.06" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          className="max-w-3xl"
        >
          <motion.p
            custom={0}
            variants={fadeUp}
            className="font-body text-[#B8102E] text-xs tracking-[0.3em] uppercase mb-6"
          >
            Strategic Execution Partner
          </motion.p>

          <motion.h1
            custom={1}
            variants={fadeUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6"
          >
            Your Utility Player for{' '}
            <span className="text-[#B8102E]">Mission-Critical</span>{' '}
            Execution
          </motion.h1>

          <motion.p
            custom={2}
            variants={fadeUp}
            className="font-body text-base md:text-lg text-[#A3A3A3] max-w-2xl mb-10 leading-relaxed"
          >
            Cole Utilities delivers agile, cross-functional solutions for federal,
            state, and local initiatives — stepping in wherever the mission demands.
          </motion.p>

          <motion.div
            custom={3}
            variants={fadeUp}
            className="flex flex-wrap gap-4"
          >
            <Button
              data-testid="hero-partner-btn"
              onClick={scrollToContact}
              className="bg-[#B8102E] text-white hover:bg-[#97021C] font-body font-semibold text-sm px-8 h-12 rounded-full transition-all duration-300 btn-sweep"
            >
              Partner With Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="hero-capability-btn"
              variant="outline"
              asChild
              className="border-[#2A2A2A] text-white hover:border-[#B8102E] hover:text-[#B8102E] font-body font-medium text-sm px-8 h-12 rounded-full transition-all duration-300 bg-transparent"
            >
              <a href="/capability-statement.pdf" download>
                <Download className="mr-2 h-4 w-4" />
                View Capabilities Statement
              </a>
            </Button>
          </motion.div>
        </motion.div>

        <motion.div
          custom={4}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-20 pt-8 border-t border-[#2A2A2A]"
        >
          <p className="font-body text-sm text-[#737373] italic max-w-xl">
            "Cole Utilities is the execution platform that plays every position — so your mission never slows down."
          </p>
        </motion.div>
      </div>
    </section>
  );
}
